export const periods = [
  { seconds: 1, one: 'seconde', many: 'secondes' },
  { seconds: 60, one: 'minute', many: 'minutes' },
  { seconds: 3600, one: 'heure', many: 'heures' },
  { seconds: 86400, one: 'jour', many: 'jours' },
];

// rateLimit: -1/-1 = no API access, 0/0 = unlimited (api/rateLimiter.js).
export function apiAccess(rateLimit) {
  const requests = rateLimit?.requests ?? -1;
  const timeframe = rateLimit?.timeframe ?? -1;
  const defaults = { requests: 100, amount: 1, period: 3600 };
  if ((requests < 0) || (timeframe < 0)) return { mode: 'none', ...defaults };
  if ((requests === 0) && (timeframe === 0)) return { mode: 'unlimited', ...defaults };
  const period = [ ...periods ].reverse().find((candidate) => (timeframe % candidate.seconds) === 0)?.seconds ?? 1;
  return { mode: 'limited', requests, amount: timeframe / period, period };
}

export function toRateLimit(access) {
  if (access.mode === 'none') return { requests: -1, timeframe: -1 };
  if (access.mode === 'unlimited') return { requests: 0, timeframe: 0 };
  return { requests: access.requests, timeframe: access.amount * access.period };
}

export function describeApiAccess(rateLimit) {
  const access = apiAccess(rateLimit);
  if (access.mode === 'none') return 'Aucun';
  if (access.mode === 'unlimited') return 'Illimité';
  const period = periods.find((candidate) => candidate.seconds === access.period);
  return (access.amount === 1) ? `${access.requests} / ${period.one}` : `${access.requests} / ${access.amount} ${period.many}`;
}

// all: null = left out of describeScope when the rule holds every value.
export const scopeFields = [
  { key: 'schools', label: 'Écoles', all: null },
  { key: 'sections', label: 'Sections', all: 'toutes sections' },
  { key: 'programs', label: 'Programmes', all: null },
  { key: 'specialities', label: 'Spécialités', all: null },
  { key: 'campuses', label: 'Campus', all: 'tous campus' },
  { key: 'promos', label: 'Promos', all: 'toutes promos' },
  { key: 'years', label: 'Années', all: 'toutes années' },
];

// Kept in promos by userAbilityBuilder: the promos from the current year on.
export const currentPromos = '@current';

// userAbilityBuilder treats an empty list like *: show it as « Tous ».
export function normalizeRule(rule) {
  return Object.fromEntries(scopeFields.map(({ key }) => [ key, (rule?.[key]?.length > 0) ? rule[key] : [ '*' ] ]));
}

const unrestricted = (rule) => scopeFields.every(({ key }) => rule[key].includes('*'));

// No rule: no student (a user without profile sees none).
export function describeScope(rules) {
  if (rules.length === 0) return 'Aucun étudiant';
  if (rules.map(normalizeRule).some(unrestricted)) return 'Tous les étudiants';
  const shown = (values) => values.map((value) => (value === currentPromos) ? 'promos en cours' : value).join(', ');
  return rules.map(normalizeRule).map((rule) => scopeFields.map(({ key, all }) => rule[key].includes('*') ? all : shown(rule[key])).filter((part) => part !== null).join(' · ')).join(' ou ');
}

export const adminActions = [ { key: 'read', label: 'voir' }, { key: 'create', label: 'créer' }, { key: 'update', label: 'modifier' }, { key: 'delete', label: 'supprimer' } ];
export const adminGroups = [ { key: 'users', label: 'Users' }, { key: 'permissions', label: 'Profils de droits' } ];

export const futureRights = [
  { key: 'pedagoStatus', label: 'Statut pédago', description: 'Modifier le statut pédago de l\'an prochain, tant que la réinscription n\'est pas lancée.' },
  { key: 'pedagoStatusAdmin', label: 'Statut pédago admin', description: 'Modifier le statut pédago de l\'an prochain, même après le lancement de la réinscription.' },
  { key: 'destination', label: 'Destination', description: 'Choisir la destination de l\'an prochain.' },
  { key: 'reenroll', label: 'Réinscription', description: 'Lancer la réinscription dans Salesforce sur une session.' },
];

const pickRights = (rights, list) => Object.fromEntries(list.map(({ key }) => [ key, rights?.[key] === true ]));

export function pickAdmin(admin) {
  return {
    users: pickRights(admin?.users, adminActions),
    permissions: pickRights(admin?.permissions, adminActions),
    future: pickRights(admin?.future, futureRights),
  };
}

const everyAdminFlag = (admin) => adminGroups.every(({ key: group }) => adminActions.every(({ key }) => admin[group][key]));

// All eight flags read « Gestion des users »; otherwise what is set is listed as it is.
export function describeRights(admin) {
  const parts = futureRights.filter(({ key }) => admin.future[key]).map(({ label }) => label);
  if (everyAdminFlag(admin)) parts.push('Gestion des users');
  else
    for (const { key: group, label } of adminGroups) {
      const actions = adminActions.filter(({ key }) => admin[group][key]).map((action) => action.label);
      if (actions.length > 0) parts.push(`${label} : ${actions.join(', ')}`);
    }
  return (parts.length > 0) ? parts.join(' · ') : 'Aucun droit';
}

// The rights a user really has: userAbilityBuilder grants what any rule of any of its profiles grants, and nothing without a profile.
// A profile without rule gives every student.
export function effectiveRights(profiles) {
  const rules = profiles.flatMap((profile) => (profile.permissions?.length > 0) ? profile.permissions : [ {} ]).map(normalizeRule);
  const admins = profiles.map((profile) => pickAdmin(profile.admin));
  const admin = pickAdmin(null);
  for (const group of Object.keys(admin))
    for (const key of Object.keys(admin[group]))
      admin[group][key] = admins.some((current) => current[group][key]);
  return { rules, admin };
}

const carriedByOther = (profile, login, users) => users.some((user) => (user.login !== login) && user.permissions.some(({ _id }) => _id === profile._id));

// The personal profile of a user: named after its login and carried by nobody else. Every other profile is shared.
export function splitProfiles(user, users) {
  const personal = user.permissions.find((profile) => (profile.name === user.login) && !carriedByOther(profile, user.login, users)) ?? null;
  return { shared: user.permissions.filter((profile) => profile !== personal), personal };
}

export function sharedProfiles(permissions, users) {
  return permissions.filter((profile) => !users.some((user) => (user.login === profile.name) && (splitProfiles(user, users).personal?._id === profile._id)));
}

export function describeProfiles({ shared, personal }) {
  const names = shared.map(({ name }) => name).join(', ');
  if (personal === null) return (names === '') ? '—' : names;
  return (names === '') ? 'Droits propres' : `${names} + droits propres`;
}

// A shared profile named after a login would read as that user's personal profile.
export function profileNameError(name, profile, users, permissions) {
  const trimmed = name.trim();
  if ((trimmed === '') || (trimmed === profile?.name)) return null;
  if (users.some(({ login }) => login === trimmed)) return 'Ce nom est le login d\'un user : choisissez-en un autre.';
  if (permissions.some((current) => current.name === trimmed)) return 'Un profil porte déjà ce nom.';
  return null;
}

// The profile named after a login: the user's own, or one left over that nobody carries (back names are unique).
// shared: another user carries it, so changing it would change that user's rights too.
export function findPersonalProfile(user, login, users, permissions) {
  const profile = [ ...(user?.permissions ?? []), ...users.flatMap((current) => current.permissions), ...permissions ].find((current) => current.name === login) ?? null;
  const shared = (profile !== null) && users.some((current) => (current.login !== login) && current.permissions.some(({ _id }) => _id === profile._id));
  return { profile, shared };
}

const successes = {
  saveUser: 'Le user a bien été enregistré',
  deleteUser: 'Le user a bien été supprimé',
  regenerateApiKey: 'La clé API a bien été régénérée',
  createPermission: 'Le profil a bien été créé',
  updatePermission: 'Le profil a bien été modifié',
  deletePermission: 'Le profil a bien été supprimé',
};

export function adminMessage({ thunk, status, error }) {
  if (status === 'success') return successes[thunk] ?? '';
  if (thunk.startsWith('fetch')) return 'Impossible de charger les données d\'administration';
  const reason = error?.data?.reason;
  if (reason === 'shared') return `Le profil de droits "${error.data.name}" est porté par d'autres users : impossible de le modifier ici.`;
  if (reason === 'assigned') return `Ce profil est attribué à ${error.data.users} user${(error.data.users > 1) ? 's' : ''}`;
  if ((reason === 'exists') && thunk.endsWith('Permission')) return 'Un profil porte déjà ce nom';
  if (reason === 'exists') return (error.step === 'permission') ? 'Un profil porte déjà le nom de ce user' : 'Ce login a déjà accès à ISEE';
  if (reason === 'self') return (thunk === 'deleteUser') ? 'Vous ne pouvez pas supprimer votre propre compte' : 'Cette modification vous retirerait vos droits d\'administration';
  if ((error?.status === 403) && (reason === 'rights')) return 'Vous ne pouvez pas donner, modifier ou retirer des droits que vous n\'avez pas vous-même.';
  if (error?.status === 403) return 'Vous n\'avez pas le droit de faire cette action';
  if (error?.status === 404) return 'Cet élément n\'existe plus, rechargez la page';
  if (error?.status === 400) return 'Les informations saisies ne sont pas valides';
  return 'Une erreur est survenue';
}
